import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, User, Users, Check, ArrowRight, ArrowLeft, Plus, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const steps = [
  { id: 1, title: "Workspace", icon: Building2, desc: "Name your household workspace" },
  { id: 2, title: "Principal", icon: User, desc: "Add the person you manage" },
  { id: 3, title: "Linked people", icon: Users, desc: "Add family & staff" },
];

export default function Setup() {
  const { login } = useFgp();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [ws, setWs] = useState("The Vance Household");
  const [principal, setPrincipal] = useState({ name: "Jonathan Ashworth-Vance", title: "Founder & Chairman", location: "Kensington, London" });
  const [linked, setLinked] = useState([
    { name: "Eleanor Vance", relation: "Spouse" },
    { name: "Sophie Vance", relation: "Daughter" },
  ]);
  const [newName, setNewName] = useState("");
  const [newRel, setNewRel] = useState("");

  const next = () => (step < 3 ? setStep(step + 1) : finish());
  const back = () => setStep(step - 1);
  const finish = () => { login(); navigate("/app/dashboard"); };

  const addLinked = () => {
    if (!newName.trim()) return;
    setLinked([...linked, { name: newName, relation: newRel || "Linked person" }]);
    setNewName(""); setNewRel("");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="h-16 flex items-center px-6 border-b border-slate-100 bg-white">
        <Logo />
        <button onClick={() => navigate("/login")} className="ml-auto text-sm text-slate-400 hover:text-slate-600">Skip to sign in</button>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          {/* Steps indicator */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-10">
            {steps.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                    step > s.id ? "bg-emerald-500 text-white" : step === s.id ? "bg-neutral-600 text-white" : "bg-slate-100 text-slate-400"
                  }`}>
                    {step > s.id ? <Check className="h-4 w-4" /> : s.id}
                  </div>
                  <span className={`text-sm font-medium hidden sm:block ${step >= s.id ? "text-slate-800" : "text-slate-400"}`}>{s.title}</span>
                </div>
                {i < steps.length - 1 && <div className={`h-px w-8 sm:w-12 ${step > s.id ? "bg-emerald-400" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>

          <motion.div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8" layout>
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="setup-step-1">
                  <h2 className="font-heading text-2xl font-semibold text-slate-900">Create your workspace</h2>
                  <p className="mt-1 text-sm text-slate-500">This is the home for everything you manage.</p>
                  <div className="mt-6">
                    <Label className="text-slate-700">Workspace name</Label>
                    <Input value={ws} onChange={(e) => setWs(e.target.value)} className="mt-1.5" data-testid="setup-ws-name" />
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="setup-step-2">
                  <h2 className="font-heading text-2xl font-semibold text-slate-900">Add your principal</h2>
                  <p className="mt-1 text-sm text-slate-500">The person at the centre of this household.</p>
                  <div className="mt-6 space-y-4">
                    <div><Label className="text-slate-700">Full name</Label><Input value={principal.name} onChange={(e) => setPrincipal({ ...principal, name: e.target.value })} className="mt-1.5" data-testid="setup-principal-name" /></div>
                    <div><Label className="text-slate-700">Title / role</Label><Input value={principal.title} onChange={(e) => setPrincipal({ ...principal, title: e.target.value })} className="mt-1.5" /></div>
                    <div><Label className="text-slate-700">Location</Label><Input value={principal.location} onChange={(e) => setPrincipal({ ...principal, location: e.target.value })} className="mt-1.5" /></div>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="setup-step-3">
                  <h2 className="font-heading text-2xl font-semibold text-slate-900">Link people</h2>
                  <p className="mt-1 text-sm text-slate-500">Add family members and household staff connected to {principal.name.split(" ")[0]}.</p>
                  <div className="mt-6 space-y-2">
                    {linked.map((p, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5" data-testid={`setup-linked-${i}`}>
                        <div className="h-8 w-8 rounded-full bg-neutral-50 text-neutral-600 flex items-center justify-center text-sm font-semibold">{p.name[0]}</div>
                        <div className="flex-1"><p className="text-sm font-medium text-slate-800">{p.name}</p><p className="text-xs text-slate-400">{p.relation}</p></div>
                        <button onClick={() => setLinked(linked.filter((_, idx) => idx !== i))} className="text-slate-300 hover:text-rose-500"><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <Input placeholder="Name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="setup-new-name" />
                    <Input placeholder="Relation (e.g. Nanny)" value={newRel} onChange={(e) => setNewRel(e.target.value)} className="sm:w-48" />
                    <Button variant="outline" onClick={addLinked} data-testid="setup-add-linked" className="gap-1.5"><Plus className="h-4 w-4" /> Add</Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-8 flex items-center justify-between">
              {step > 1 ? (
                <Button variant="ghost" onClick={back} className="gap-1.5 text-slate-500" data-testid="setup-back"><ArrowLeft className="h-4 w-4" /> Back</Button>
              ) : <span />}
              <Button onClick={next} className="bg-neutral-600 hover:bg-neutral-700 gap-1.5" data-testid="setup-next">
                {step === 3 ? "Enter workspace" : "Continue"} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
